// Instant-read pre-LLM risk banner and its chip helpers.
// Extracted from DecisionEnginePage.js during Phase 1 consolidation —
// no logic or visual changes. Passive, no state.
import React from 'react';

export const RISK_CHIP_STYLE = {
  high: 'bg-red-100 text-red-800 border-red-200 dark:bg-red-950/40 dark:text-red-300 dark:border-red-900',
  overload: 'bg-red-100 text-red-800 border-red-200 dark:bg-red-950/40 dark:text-red-300 dark:border-red-900',
  moderate: 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-900',
  underload: 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-900',
  low: 'bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-900',
  optimal: 'bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-900',
  available: 'bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-900',
  high_confidence: 'bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-900',
  medium: 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-900',
  insufficient: 'bg-gray-100 text-gray-600 border-gray-200 dark:bg-gray-800/40 dark:text-gray-400 dark:border-gray-700',
  unknown: 'bg-gray-100 text-gray-500 border-gray-200 dark:bg-gray-800/40 dark:text-gray-400 dark:border-gray-700',
  pending: 'bg-gray-100 text-gray-400 border-gray-200 dark:bg-gray-800/40 dark:text-gray-500 dark:border-gray-700 animate-pulse',
};

export const RISK_CHIP_LABEL = {
  // Injury / fatigue share the three-tier labels.
  high: 'High',
  moderate: 'Moderate',
  low: 'Low',
  // Load.
  overload: 'Overload',
  optimal: 'Optimal',
  underload: 'Underload',
  // GPS.
  available: '✓',
  insufficient: '–',
  // Confidence.
  high_confidence: 'High',
  medium: 'Medium',
  // Fallback.
  unknown: '—',
};

export const RiskChip = ({ label, value, confidenceChip = false, testId }) => {
  const isPending = value === 'pending';
  const styleKey = isPending
    ? 'pending'
    : (confidenceChip && value === 'high')
        ? 'high_confidence'
        : value;
  const labelKey = (confidenceChip && value === 'high') ? 'high_confidence' : value;
  const cls = RISK_CHIP_STYLE[styleKey] || RISK_CHIP_STYLE.unknown;
  const v = isPending ? '…' : (RISK_CHIP_LABEL[labelKey] || '—');
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium ${cls}`}
      data-testid={testId}
    >
      <span className="uppercase tracking-wide text-[10px] opacity-70">{label}</span>
      <span>{v}</span>
    </span>
  );
};

export const RiskBanner = ({ flags, loading = false }) => {
  const f = loading
    ? {
        injury_risk: 'pending',
        fatigue_risk: 'pending',
        load_risk: 'pending',
        gps_status: 'pending',
        confidence: 'pending',
      }
    : (flags || {
        injury_risk: 'unknown',
        fatigue_risk: 'unknown',
        load_risk: 'unknown',
        gps_status: 'unknown',
        confidence: 'unknown',
      });
  return (
    <div
      className="flex flex-wrap items-center gap-1.5 px-1 pt-0.5"
      data-testid="risk-banner"
      aria-label="Pre-decision risk signals"
    >
      <span className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500 mr-1">
        Instant read
      </span>
      <RiskChip label="Injury" value={f.injury_risk} testId="risk-chip-injury" />
      <RiskChip label="Fatigue" value={f.fatigue_risk} testId="risk-chip-fatigue" />
      <RiskChip label="Load" value={f.load_risk} testId="risk-chip-load" />
      <RiskChip label="GPS" value={f.gps_status} testId="risk-chip-gps" />
      <RiskChip label="Confidence" value={f.confidence} confidenceChip testId="risk-chip-confidence" />
    </div>
  );
};
